import { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Save, Trash2, Plus, X, Link2, ChevronDown, ChevronRight, Store,
} from 'lucide-react';
import {
  getShop, saveShopSlot, deleteShopSlot, saveShopMeta, saveNpcScript,
  type ShopResponse, type ShopSlot, type PriceItem, type NpcScript, type EventSummary, type GameItem,
} from '@/shared/services/eventManagerService';
import { useToast } from '@/shared/contexts/ToastContext';
import { GameItemIcon, GameItemPicker, ItemHover, ItemKindField, ItemKindInline } from './GameItem';

const COLS = 8, ROWS = 16, TOTAL = COLS * ROWS;

const emptySlot = (idx: number): ShopSlot => ({
  slotIdx: idx, itemKindIdx: 0, itemOption: '0', itemDurationIdx: 0,
  priceAlz: '0', pricePoint: 0, itemPriceId: null, expirationTime: null,
  dailyLimit: 0, weeklyLimit: 0, requiredItems: [], priceGroupRefCount: 0,
});

export default function ItemCollectionEditor({ event }: { event: EventSummary }) {
  const { notify } = useToast();
  const id = event.eventId;
  const [shop, setShop] = useState<ShopResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setShop(await getShop(id)); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro ao carregar loja'); }
    finally { setLoading(false); }
  }, [id, notify]);
  useEffect(() => { load(); }, [load]);

  if (loading || !shop) return <div className="py-16 flex justify-center text-white/40"><Loader2 size={20} className="animate-spin" /></div>;

  const byIdx = new Map(shop.slots.map(s => [s.slotIdx, s]));
  const used = shop.slots.length;
  const current = sel !== null ? (byIdx.get(sel) ?? emptySlot(sel)) : null;

  return (
    <div className="space-y-6">
      <NpcSection shop={shop} eventId={id} onSaved={load} />

      <div className="surface-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
            <Store size={15} className="text-[#00e1d7]" /> Event Shop
          </h2>
          <span className="text-xs text-white/50">{used} / {TOTAL} slots</span>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
          {/* grid de slots */}
          <div className="grid grid-cols-8 gap-1.5 content-start">
            {Array.from({ length: TOTAL }, (_, i) => {
              const s = byIdx.get(i);
              const active = sel === i;
              return (
                <button key={i} onClick={() => setSel(i)}
                  className={`relative aspect-square rounded-md border transition cursor-pointer
                    ${active ? 'border-[#00e1d7] ring-1 ring-[#00e1d7]/40'
                      : s ? 'border-white/[0.08] hover:border-[#00e1d7]/50 bg-[#0a0e14]'
                          : 'border-dashed border-white/[0.08] hover:border-white/20 bg-transparent'}`}>
                  <span className="absolute top-0.5 left-1 text-[8px] text-white/25 font-mono z-10">{i}</span>
                  {s ? (
                    <ItemHover kind={s.itemKindIdx} option={s.itemOption} className="absolute inset-0">
                      <GameItemIcon itemId={s.itemKindIdx} fill />
                    </ItemHover>
                  ) : <span className="absolute inset-0 flex items-center justify-center text-white/15 text-lg">+</span>}
                  {s && s.requiredItems.length > 0 && (
                    <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-400" title="preço com item" />
                  )}
                </button>
              );
            })}
          </div>

          {/* editor do slot */}
          <div className="lg:sticky lg:top-4 self-start">
            {current === null ? (
              <div className="rounded-xl border border-dashed border-white/[0.1] p-8 text-center text-xs text-white/40">
                Selecione um slot para editar
              </div>
            ) : (
              <SlotEditor key={sel} eventId={id} slot={current} exists={byIdx.has(sel!)}
                onSaved={async () => { await load(); }}
                onDeleted={async () => { setSel(null); await load(); }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------ NPC / Script / nome da loja */
function NpcSection({ shop, eventId, onSaved }: { shop: ShopResponse; eventId: number; onSaved: () => void }) {
  const { notify } = useToast();
  const sc = shop.npcScript;
  const [name, setName] = useState(shop.shopName ?? '');
  const [f, setF] = useState<NpcScript>({
    worldIdx: sc?.worldIdx ?? 1, npcIdx: sc?.npcIdx ?? 49,
    script1: sc?.script1 ?? '', script2: sc?.script2 ?? '', script3: sc?.script3 ?? '',
    script4: sc?.script4 ?? '', script5: sc?.script5 ?? '', scriptDesc: sc?.scriptDesc ?? '',
  });
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof NpcScript, v: any) => setF(s => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await saveShopMeta(eventId, { shopName: name });
      await saveNpcScript(eventId, f);
      notify('success', 'NPC / loja salvos'); onSaved();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide">NPC / Loja</h2>
        <button onClick={save} disabled={busy}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold btn-primary transition cursor-pointer disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvar
        </button>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2"><Lbl label="Título da loja"><input value={name} onChange={(e) => setName(e.target.value)} className={inp} /></Lbl></div>
        <Lbl label="World"><input type="number" value={f.worldIdx} onChange={(e) => set('worldIdx', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
        <Lbl label="NPC"><input type="number" value={f.npcIdx} onChange={(e) => set('npcIdx', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Lbl label="Mensagem principal (Script1)"><textarea value={f.script1 ?? ''} onChange={(e) => set('script1', e.target.value)} rows={2} className={`${inp} resize-y`} /></Lbl>
        <Lbl label="Descrição interna (ScriptDesc)"><input value={f.scriptDesc ?? ''} onChange={(e) => set('scriptDesc', e.target.value)} className={inp} /></Lbl>
      </div>
      <button onClick={() => setMore(m => !m)} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 mt-3 transition cursor-pointer">
        {more ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Scripts adicionais
      </button>
      {more && (
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {([2, 3, 4, 5] as const).map(n => (
            <Lbl key={n} label={`Script${n}`}>
              <textarea value={(f as any)[`script${n}`] ?? ''} onChange={(e) => set(`script${n}` as keyof NpcScript, e.target.value)} rows={2} className={`${inp} resize-y`} />
            </Lbl>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------ Editor de 1 slot */
function SlotEditor({
  eventId, slot, exists, onSaved, onDeleted,
}: { eventId: number; slot: ShopSlot; exists: boolean; onSaved: () => void; onDeleted: () => void }) {
  const { notify } = useToast();
  const [s, setS] = useState<ShopSlot>(slot);
  const [busy, setBusy] = useState(false);
  const [pickPrice, setPickPrice] = useState(false);
  const set = (k: keyof ShopSlot, v: any) => setS(p => ({ ...p, [k]: v }));
  const shared = (s.priceGroupRefCount ?? 0) > 1;

  const save = async () => {
    if (!s.itemKindIdx) { notify('error', 'Escolha um item'); return; }
    setBusy(true);
    try { await saveShopSlot(eventId, s.slotIdx, s); notify('success', `Slot ${s.slotIdx} salvo`); onSaved(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Remover o slot ${s.slotIdx}?`)) return;
    setBusy(true);
    try { await deleteShopSlot(eventId, s.slotIdx); notify('success', 'Slot removido'); onDeleted(); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); setBusy(false); }
  };
  const addPrice = (it: GameItem) => set('requiredItems', [...s.requiredItems, { itemKindIdx: it.itemId, itemOption: '0', itemCount: 1 }]);
  const setPrice = (i: number, patch: Partial<PriceItem>) => set('requiredItems', s.requiredItems.map((p, j) => j === i ? { ...p, ...patch } : p));
  const rmPrice = (i: number) => set('requiredItems', s.requiredItems.filter((_, j) => j !== i));

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0b1119] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-white uppercase tracking-wide">Slot {s.slotIdx}</span>
        {exists && <button onClick={del} disabled={busy} className="text-red-400/80 hover:text-red-400 transition cursor-pointer"><Trash2 size={15} /></button>}
      </div>

      {/* item (kind editável + picker + stepper +N) */}
      <ItemKindField value={s.itemKindIdx} onChange={(v) => set('itemKindIdx', v)} />
      <div className="grid grid-cols-2 gap-2">
        <Lbl label="Item Option"><input value={s.itemOption} onChange={(e) => set('itemOption', e.target.value)} className={`${inp} font-mono`} /></Lbl>
        <Lbl label="Duration Idx"><input type="number" value={s.itemDurationIdx} onChange={(e) => set('itemDurationIdx', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
      </div>

      {/* preço */}
      <div className="pt-2 border-t border-white/[0.05]">
        <p className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">Preço</p>
        <div className="grid grid-cols-2 gap-2">
          <Lbl label="Alz"><input value={s.priceAlz} onChange={(e) => set('priceAlz', e.target.value.replace(/\D/g, '') || '0')} className={`${inp} font-mono`} /></Lbl>
          <Lbl label="Point / WEXP"><input type="number" value={s.pricePoint} onChange={(e) => set('pricePoint', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
        </div>
        {shared && (
          <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/25">
            <Link2 size={13} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-200/90">Preço compartilhado por {s.priceGroupRefCount} slots. Alterar aqui cria uma cópia independente (não afeta os outros).</p>
          </div>
        )}
        <div className="space-y-1.5 mt-2">
          {s.requiredItems.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <ItemKindInline value={p.itemKindIdx} onChange={(v) => setPrice(i, { itemKindIdx: v })} />
              <input type="number" min={1} value={p.itemCount} onChange={(e) => setPrice(i, { itemCount: Number(e.target.value) })}
                title="quantidade" className="w-14 bg-[#0d141b] border border-white/[0.06] rounded px-2 py-1 text-xs text-white font-mono" />
              <button onClick={() => rmPrice(i)} className="text-white/40 hover:text-red-400 transition cursor-pointer shrink-0"><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => setPickPrice(true)} className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-white/[0.1] text-[11px] text-white/50 hover:text-white hover:border-white/25 transition cursor-pointer">
            <Plus size={13} /> Adicionar item ao preço
          </button>
        </div>
      </div>

      {/* limites */}
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/[0.05]">
        <Lbl label="Daily Limit"><input type="number" value={s.dailyLimit} onChange={(e) => set('dailyLimit', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
        <Lbl label="Weekly Limit"><input type="number" value={s.weeklyLimit} onChange={(e) => set('weeklyLimit', Number(e.target.value))} className={`${inp} font-mono`} /></Lbl>
      </div>

      <button onClick={save} disabled={busy}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg btn-primary font-bold text-sm transition cursor-pointer disabled:opacity-40">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar slot
      </button>

      <GameItemPicker open={pickPrice} onClose={() => setPickPrice(false)} title="Item necessário no preço"
        onPick={addPrice} />
    </div>
  );
}

const inp = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-2.5 py-1.5 text-sm text-white';
function Lbl({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[10px] text-white/50 mb-1 block">{label}</label>{children}</div>;
}
